(ns quuppa.peweb.peinfo-locators
  (:require [clojure.string :as s]
            [goog.string :as gstring]
            [goog.string.format]
            [goog.object :as gobj]
            [linked.core :as l]
            [komponentit.timeago :as timeago]
            [malli.util :as mu]
            [quuppa.qsm.permissions-access :as permissions-access]
            [quuppa.ui.button :as button]
            [re-frame.core :as rf :refer [reg-sub subscribe]]
            [reagent.core :as r]
            ["react-window" :refer [FixedSizeList]]
            ["react-use-measure$default" :as useMeasure]
            ["react-virtualized-auto-sizer$default" :as AutoSizer]
            [quuppa.date-time.core :as date-time]
            [quuppa.pe-domain :as pe-domain]
            [quuppa.peweb.tables :as tables]
            [quuppa.peweb.peinfo-events :as e]
            [quuppa.qsm.subs :as qsm-subs]
            [quuppa.qsm.site.subs :as ss]
            [quuppa.ui.layout :as layout]
            [quuppa.ui.icons :refer [icon]]
            [quuppa.utils :as utils]
            [quuppa.qsm.permissions :refer [has-manage-permission-to-resource?]]))

(defn time->color-class
  "Coloring class for time value. Like `getColor` in PE core."
  [time-value]
  (cond
    (< time-value 0) "bg-warning"
    (< time-value 5000) "bg-success"
    (< time-value 60000) "bg-warning"
    :else "bg-danger"))

(defn last-packet->display-value [value]
  (if (vector? value)
    ;; Legacy value
    (let [[ago delta] value]
      (when ago
        [:span.p-1
         {:class (time->color-class delta)}
         ago]))
    (when value
      [timeago/timeago-2
       {:value (date-time/temporal->native-date value)
        :format-fn (fn [{:keys [diff-ms] :as x}]
                     [:span
                      {:class (time->color-class diff-ms)}
                      (date-time/duration-string x)])}])))

(defn conn-status->class [connection-status-enum]
  (case connection-status-enum
    :Good "bg-primary"
    :Medium "bg-warning"
    (:SetupError :Bad) "bg-danger"
    :NoConnection "bg-light"
    nil))

(defn command-status-name [k]
  (case k
    :WaitingForStatus "Waiting"
    :WaitingForReply "Ok"
    :Ok "Ok"
    nil nil
    (name k)))

(defn render-number [n]
  (when n
    (gstring/format "%.2f" n)))

(defn render-degrees
  "If we don't have value (nil) return nil and the table render will add empty marker.
  If we have value that says we don't know the value, render warning.
  Else render the value as degrees"
  [n]
  (cond
    (nil? n) nil

    ;; TODO: JS MIN_VALUE is the smallest representable POSITIVE number, i.e. as close to zero
    ;; The value from PE core is Java max negative integer probably.
    ;; We should handle these values in the backend in some environment agnostic way.
    ;; E.g. :locator-info :azimuth-rad already checks for Double/MIN_VALUE in the QPE-next
    ;; and returns nil, but other fields probably don't have the same check.

    ; (<= n js/Number.MIN_VALUE) "Unknown"
    :else (render-number (utils/radians->degrees n))))

(def locator-sidebar-tabs
  [{:id "layout" :label "Layout"}
   {:id "radio" :label "Radio"}
   {:id "wireless" :label "Wireless"}
   {:id "network" :label "Network"}])

(def locator-columns-main
  (l/map
    :name
    {:value-fn :name
     :sortable true
     :width    5
     :label    "Name"
     :always-selected true}

    :id
    {:value-fn :id
     :render-fn (fn [s locator _props]
                  (if (:has-dummy-id locator)
                    [:i "Not identified"]
                    s))
     :sortable true
     :width    5
     :label    "ID"}

    :cs
    {:value-fn ::coordinate-system-name
     :sortable true
     :width    5
     :label    "Coordinate system"}

    :area
    {:value-fn (fn [locator]
                 (let [grids (->> (or (:trackingareas locator)
                                      ;; TODO: Legacy name, can be removed later.
                                      (:grids locator))
                                  (map :name-id))]
                   (if (empty? grids)
                     [:span.p-1.bg-warning "*noarea*"]
                     (s/join "," grids))))
     :sortable true
     :width    10
     :label    "Area"}

    :connection
    {:value-fn #(or (:connection-status %) (:command-status %))
     :render-fn command-status-name
     :class-fn (fn [value _ _]
                 (case value
                   :Discovered "bg-light"
                   :Failed "bg-danger"
                   :Ok "bg-success"
                   (:Waiting :FWUpgrade :NotConnectable) "bg-warning"
                   ;; sc-33730 old QPE versions do not send connection-status, but 'locator command status'.
                   ;; bringing back the legacy values to UI, as it is now blank if :connection-status not present.
                   :WaitingForReply "bg-success"
                   :WaitingForStatus "bg-warning"
                   nil))
     :sortable true
     :width    5
     :label    "Connection"}

    :hw-type
    {:value-fn :hw-type
     :sortable true
     :width    5
     :label    "Type"}))

(defn format-location [k v]
  (when-let [x (get-in v [:converted-location k])]
    (gstring/format "%.2f" x)))

(def locator-columns-layout
  (l/map
    :uptime
    {:value-fn  #(get-in % [:locator-info :last-reset-time])
     :render-fn (fn [t]
                  ;; Legacy value
                  (if (number? t)
                    t
                    (date-time/format-instant t)))
     :sortable true
     :label    "Previous reset"}

    :mode
    {:value-fn :mode
     :sortable true
     :label    "Mode"}

    :acc-az-corr
    {;; Matches classic probably?
     ;; :value-fn #(get-in % [:locator-info :accelerometer-data :az-corr])
     ;; sc-27068: alignment orientation value set in QMD/QSP
     :value-fn #(get-in % [:orientation :azimuth-rad])
     :render-fn render-degrees
     :sortable true
     :label    "Orientation, rotation (deg)"}

    :acc-tilt-ele
    {:value-fn #(get-in % [:locator-info :accelerometer-data :tilt-ele])
     :render-fn render-degrees
     :sortable true
     :label    "Orientation, elevation (deg)"}

    :acc-az-corr-error
    {:value-fn (fn [l]
                 (let [az-corr (get-in l [:locator-info :accelerometer-data :az-corr])
                       azimuth-rad (get-in l [:orientation :azimuth-rad])
                       tilt-elevation-rad (get-in l [:orientation :tilt-elevation-rad])
                       limit-rad (utils/degrees->radians 10)]
                   (cond
                     ; (<= az-corr js/Number.MIN_VALUE) nil

                     (and azimuth-rad
                          (> tilt-elevation-rad limit-rad)
                          (> (- Math/PI azimuth-rad) limit-rad))
                     (Math/abs (utils/mod-pi (utils/radians->degrees (- az-corr azimuth-rad)))))))
     :sortable true
     :render-fn render-degrees
     :label    "Orientation error, rotation (deg)"}

    :acc-tilt-ele-error
    {:value-fn (fn [l]
                 (let [tilt-ele (get-in l [:locator-info :accelerometer-data :tilt-ele])
                       tilt-elevation-rad (get-in l [:orientation :tilt-elevation-rad])]
                   (cond
                     ; (<= tilt-ele js/Number.MIN_VALUE) nil
                     :else (when tilt-elevation-rad
                             (Math/abs (- tilt-ele tilt-elevation-rad))))))
     :render-fn render-degrees
     :sortable true
     :label    "Orientation error, elevation (deg)"}

    :override-script
    {:value-fn #(case (:script-source %)
                  :Override (:raw-script-id %)
                  "-")
     :sortable true
     :label    "Startup override"}

    :scan-mode
    {; FIXME implement full support for showing override
     ; FIXME show warning color isn't in the mode the tagcommander thinks it should be
     ;if (locator.getScanMode() == TagCommander.ins().getCurrentScanMode(locator)) return okColor;
     :value-fn (fn [l]
                 (if (= (:script-source l) :Override)
                   "fromScript, <not fully implemented>"
                   (case (:scan-mode l)
                     :Adv "Data"
                     :AoA "DF"
                     :AoA_AoD "DF"
                     :AoD "DF"
                     :AoA_Adv "DF and Data"
                     :AoA_AoD_Adv "DF and Data"
                     :AoD_Adv "DF and Data"
                     :None "None"
                     "Custom")))
     :sortable true
     :label    "Scan mode"}

    :sensitivity
    {:value-fn :fixed-sensitivity
     :render-fn render-number
     :sortable true
     :label    "Sensitivity (dB)"}

    :x
    {:value-fn (partial format-location :x)
     :sortable true
     :label    "x (m)"}

    :y
    {:value-fn (partial format-location :y)
     :sortable true
     :label    "y (m)"}

    :z
    {:value-fn (partial format-location :z)
     :sortable true
     :label    "z (m)"}

    :temperature
    {:value-fn #(get-in % [:locator-info :temperature])
     :sortable true
     :render-fn (fn [{:keys [temperature]}]
                  (render-number temperature))
     :label    "Temperature (C)"}

    :identification-status
    {:value-fn pe-domain/locator->identification-status
     :sortable true
     :label    "Identification"}

    :identification-id
    {:value-fn :id
     :sortable true
     :width    5
     :label    "ID"
     :selectable false}

    :focusing-status
    {:value-fn pe-domain/locator->focusing-status
     :sortable true
     :label    "Focusing"}

    :command-status
    {:value-fn :command-status
     :render-fn command-status-name
     :class-fn (fn [s _ _]
                 (case s
                   (:WaitingForReply :Ok) "bg-success"
                   :WaitingForStatus "bg-warning"
                   nil))
     :sortable true
     :width 5
     :label "Command"}

    :command-delay
    {:value-fn #(let [c-delay (:command-delay %)]
                  (if (> c-delay 0)
                    c-delay
                    "Unknown"))
     :sortable true
     :label    "Command delay (ms)"}))

(defn firmware-version
  [firmware-key button? value locator {:keys [qpe-dispatch qpe-subscribe]}]
  (let [{:keys [site-id project-id]} @(rf/subscribe [:nav/match :parameters :path])
        latest @(qpe-subscribe [:qpe/data :controller-state :firmware-versions firmware-key])
        current-user @(rf/subscribe [::qsm-subs/user])
        current-qsm-role (permissions-access/get-user-role current-user)
        site-entity @(rf/subscribe [::ss/site project-id site-id])]
    (if value
      [:span.w-100.d-inline-flex.flex-row.align-items-center
       [:span.flex-grow-1
        value
        (when (not= latest value)
          [:span
           " (Latest: " latest ")"])]
       (when (and button?
                  (not (= current-qsm-role "member"))
                  (has-manage-permission-to-resource? site-entity))
         [:button.btn.btn-sm.btn-primary
          {:on-click #(qpe-dispatch [:qpe/update-locator-fw (:id locator)])}
          "Update FW"])]
      "-")))

(defn firmware-class
  [firmware-key value _locator {:keys [qpe-subscribe]}]
  (let [latest @(qpe-subscribe [:qpe/data :controller-state :firmware-versions firmware-key])]
    (when value
      (if (= latest value)
        "bg-success"
        "bg-warning"))))

(def locator-columns-radio
  (l/map
    :df-last-packet
    {:value-fn #(get-in % [:df-packet-statistics :last-time])
     :render-fn last-packet->display-value
     :sortable true
     :label    "Last DF packet"}

    :last-good-packet
    {:value-fn #(get-in % [:good-packet-statistics :last-time])
     :render-fn last-packet->display-value
     :sortable true
     :label    "Last Good DF"}

    :data-last-packet
    {:value-fn #(get-in % [:data-packet-statistics :last-time])
     :render-fn last-packet->display-value
     :sortable true
     :label    "Last Data Packet"}

    :df-pps
    {:value-fn #(get-in % [:df-packet-statistics :short-pps])
     :render-fn render-number
     :sortable true
     :label    "DF pps"}

    :data-pps
    {:value-fn #(get-in % [:data-packet-statistics :short-pps])
     :render-fn render-number
     :sortable true
     :label    "Data pps"}

    :sync-per-second
    {:value-fn (fn [l]
                 (let [[value _time] (:sync-per-second l)]
                   (cond
                     (= (:mode l) :Deployment)
                     "deployment"

                     :else
                     (render-number value))))
     ;; "Sync/s (age)", current the age part isn't displayed
     :sortable true
     :label    "Sync/s"}


    :networkloss-pps
    {:value-fn (fn [locator]
                 (let [active-qpe @(subscribe [:qpe/active-qpe])
                       ;;server-info @(qpe-subscribe [:qpe/server-info])
                       ;;controller-state @(qpe-subscribe [:qpe/controller-state])
                       system-status @(subscribe [:qpe/system-status active-qpe])]
                   (cond
                     ; we should also check for TagCommander.ins().getCurrentScanMode( but roll this into mode
                     (= (:mode locator) :Deployment) "Deployment"
                     (> (get-in system-status [:turbo-packet-statistics :short-pps]) 0.1) "Turbo mode"
                     :else (get-in locator [:dropped-after-locator-packet-statistics :short-pps]))))
     :sortable true
     :label    "Network loss pps"}

    :deployment-mode
    {:value-fn (fn [locator]
                 (let [active-script (:active-script locator)
                       active-qpe @(subscribe [:qpe/active-qpe])
                       server-info @(subscribe [:qpe/server-info active-qpe])
                       controller-state @(subscribe [:qpe/controller-state active-qpe])]
                   (cond
                     ; FIXME we haven't implemented :failover-active? and it probably should be
                     ; part of controller-state
                     (and (:use-failover server-info) (not (:failover-active? server-info)))
                     "Failover not active"
                     (= (:target-mode controller-state) :Deployment) "QPE deployment"
                     (and (nil? active-script) (not (:use-failover server-info))) "NotReady"
                     (= (:mode locator) :Deployment) "Deactivate"
                     (= (:mode locator) :Moved) "Moved, Fix"
                     :else "Activate")))
     :sortable true
     :label    "Deployment mode"}

    :qxx-license-data
    {:value-fn (fn [locator]
                 (let [active-qpe       @(subscribe [:qpe/active-qpe])
                       controller-state @(subscribe [:qpe/controller-state active-qpe])
                       license          (get locator :qxx-license-data)]
                   (assoc license :deployment? (:deployment-mode? controller-state))))
     :render-fn (fn [{license-status :status :as license} locator _props]
                  (cond
                    (:deployment? license) "deployment"

                    (= (:locator-generation locator) :Qxx)
                    ;; private-key-crypter? check in the backend is disabled, it spams the log
                    (if (false? (:pk-crypter? license))
                      "No key"
                      (name license-status))

                    :else "Unknown"))
     :class-fn (fn [{license-status :status :as license} _locator _props]
                 (let [expiring-class (when-let [days (some-> (when (pos? (:time-to-expire license-status))
                                                                (:time-to-expire license-status))
                                                              (/ (* 1000 3600 24)))]
                                        (cond
                                          (< days 0.5) "bg-error"
                                          (< days 1.0) "bg-warning"))]
                   (cond
                     (false? (:pk-crypter? license)) "bg-warning"
                     (true? (:atecc-failure license)) "bg-warning"
                     (some? expiring-class) expiring-class
                     :else (case license-status
                             (:Expired :Invalid :PETooNew :NotYetActive :PETimeInPast) "bg-error"
                             (:Lite :Ok) "bg-success"
                             nil))))
     :sortable true
     :label "Licensing Data"}

    :subscription
    {:value-fn  :subscription
     :render-fn (fn [{:keys [id type days-left]} _locator _props]
                  [:span (str type " [") [:code id] "], " days-left " days left"])
     :label     "Subscription"}

    :commanded-receive-channels
    {:value-fn #(:receive-channel-str %)
     :sortable true
     :label    "Receive channels (MHz/BLE)"}

    :main-version
    {:value-fn  #(get-in % [:locator-info :main-fw-str])
     :render-fn (partial firmware-version :latestQxxMainFW true)
     :class-fn  (partial firmware-class :latestQxxMainFW)
     :sortable  true
     :label     "Main FW"}

    :radio-version
    {:value-fn  #(get-in % [:locator-info :pos-radio-fw-str])
     :render-fn (partial firmware-version :latestQxxPositioningRadioFW false)
     :class-fn  (partial firmware-class :latestQxxPositioningRadioFW)
     :sortable  true
     :label     "Pos Radio FW"}

    :com-radio-version
    {:value-fn  #(get-in % [:locator-info :com-radio-fw-str])
     :render-fn (partial firmware-version :latestQxxCommunicationRadioFW false)
     :class-fn  (partial firmware-class :latestQxxCommunicationRadioFW)
     :sortable  true
     :label     "Com Radio FW"}

    :ant-switch-version
    {:value-fn #(get-in % [:locator-info :antenna-switcher-fw-str])
     :render-fn (partial firmware-version :latestQxxAntennaSwitcherFW false)
     :class-fn (partial firmware-class :latestQxxAntennaSwitcherFW)
     :sortable true
     :label    "Ant switch FW"}))

(def locator-columns-wireless
  (l/map
    :wireless-type
    {:value-fn (comp :type :wireless)
     ;; TODO: colors
     :label "Q-Link type"}

    :wireless-status
    {:value-fn (comp :connection-status :wireless)
     :class-fn conn-status->class
     :label "Q-Link status"}

    :wireless-rssi
    {:value-fn (comp :rssi :wireless)
     :render-fn (fn [{:keys [error average median min max count] :as _rssi} locator _props]
                  (or error
                      (when (nil? (get-in locator [:wireless :hub]))
                        "-")
                      (s/join
                        "/"
                        [(gstring/format "%.1f" average)
                         median
                         min
                         max
                         count])))
     :class-fn (fn [{:keys [error] :as _value} _props {:keys [wireless] :as _locator}]
                 (if error
                   "bg-danger"
                   (if (nil? (:hub wireless))
                     "bg-light"
                     (conn-status->class (:connection-status wireless)))))
     :label "Q-Link connection RSSI (ave/median/min/max/count)"}

    :wireless-hub
    {:value-fn (comp :hub :wireless)
     :class-fn (fn [hub-name _props {:keys [connection-status] :as _locator}]
                 (when (some? hub-name)
                   (if (s/starts-with? hub-name "Aruba")
                     "bg-danger"
                     (if (= connection-status :Failed)
                       "bg-danger"
                       "bg-success"))))
     :render-fn (fn [value] (or value "-"))
     :label    "Connected hub"}

    :wireless-satellites
    {:value-fn (comp :slaves-count :wireless)
     :label "Connected satellites"}))

(def locator-columns-network
  (l/map
    :ip-address
    {:value-fn :ip-address
     :sortable true
     :label    "IP address"}

    :ethernet-mac
    {:value-fn #(get-in % [:locator-info :ethernet-mac])
     :sortable true
     :label    "Ethernet mac"}))

(def all-columns
  (-> locator-columns-main
      (into locator-columns-layout)
      (into locator-columns-radio)
      (into locator-columns-wireless)
      (into locator-columns-network)))

(def default-columns
  (into #{} (keys locator-columns-main)))

(reg-sub ::active-row?
  (fn [[_ _qpe-identity _locator-id]]
    (subscribe [:nav/match :parameters :query :locator-id]))
  (fn [active-locator-id [_ _qpe-identity locator-id]]
    (when active-locator-id
      (= active-locator-id locator-id))))

(defn locator-cell [props text]
  [:div.p1.div-table-cell
   props
   [:div.text-truncate
    text]
   ;; On cell hover, display to full value.
   ;; Useful if the value is truncated because column width isn't enough to show the full value.
   [:div.cell-full-value
    text]])

(defn- evaluate-locator-column
  "Resolves class-fn, render-fn etc values for locator columns.
  Returns config with :value, :rendered, :class keys added and respective fn keys dissociated."
  [{:keys [value-fn render-fn class-fn]
    :or   {render-fn identity} :as config}
   locator
   props]
  (let [value (value-fn locator)]
    (-> config
        (assoc :value value
               :rendered (render-fn value locator props)
               :class (when class-fn
                        (class-fn value locator props)))
        (dissoc :class-fn :value-fn :render-fn))))

(defn locator-row [selected-columns selected-columns-width get-item table-width {:keys [qpe-subscribe] :as props} {:keys [index style]}]
  (let [locator (get-item index)
        ;; FIXME: selected locator id is already on props, so every row is rendered when it changes
        active? @(qpe-subscribe [::active-row? (:id locator)])]
    [:div.d-flex.flex-row.div-table-row.cursor-pointer
     {:class    (when active?
                  "bg-secondary")
      :style    style
      :on-click #(rf/dispatch [:nav/update-query (fn [v]
                                                   (-> v
                                                       (dissoc :panel)
                                                       (assoc :locator-id (:id locator))))])}
     (for [[column {:keys [width] :as config}] all-columns
           :when (contains? selected-columns column)
           :let [{:keys [rendered class]} (evaluate-locator-column config locator props)]]
       ^{:key column}
       [locator-cell
        {:class class
         :style {:width (tables/width-fn table-width selected-columns-width width)}}
        rendered])]))

(reg-sub
  :current-project/all-locators
  (fn [[_ qpe-identity]]
    [(rf/subscribe [:qpe/data qpe-identity :project-state :runtime-locators])
     (rf/subscribe [:qpe/data qpe-identity :project-state :locators])
     (rf/subscribe [:qpe/data qpe-identity :current-project :cs-maps])])
  (fn [[runtime-locators project-locators-runtime-info project-cs] [_ _qpe-identity]]
    ;; Combine project locators
    ;; with runtime locators (seen on the network, not on the project)
    (concat
      (mapcat (fn [cs]
                (map (fn [static]
                       (-> static
                           ;; Merge the changing locator info to static locator info.
                           (merge (get project-locators-runtime-info (:id static)))
                           ;; Store CS name here, because not efficiently available on
                           ;; :value-fn otherwise (unless added in the backend)
                           (assoc ::coordinate-system-name (:name cs))))
                     (vals (:locator-maps cs))))
              (vals project-cs))
      (vec (vals runtime-locators)))))

(reg-sub
  ::locator-by-id
  (fn [[_ qpe-identity _search-string]]
    (rf/subscribe [:current-project/all-locators qpe-identity]))
  (fn [locators [_ _qpe-identity locator-id]]
    (first (filter #(= locator-id (:id %)) locators))))

;; Find different values for given locator property
;; {value uses}
;; Used for filters sidepanel and locator status shortcuts on the table header
(reg-sub
  ::available-filter-values
  (fn [[_ qpe-identity _]]
    [(subscribe [:current-project/all-locators qpe-identity])])
  (fn [[locators] [_ _qpe-identity k]]
    (frequencies (keep k locators))))

(rf/reg-event-db
  ::add-filter
  (fn [db [_ qpe-identity field filter-name on-off]]
    (if on-off
      (update-in db [:qpes qpe-identity :ui :locator-filters field] (fnil conj #{}) filter-name)
      (update-in db [:qpes qpe-identity :ui :locator-filters field] (fnil disj #{}) filter-name))))

(rf/reg-event-db
  ::clear-filters
  (fn [db [_ qpe-identity]]
    (assoc-in db [:qpes qpe-identity :ui :locator-filters] {})))

(reg-sub
  ::all-filters-enabled
  (fn [db [_ qpe-identity]]
    (get-in db [:qpes qpe-identity :ui :locator-filters])))

(defn does-match-filters [locator filters]
  (every? (fn [[filter-name filter-values]]
            (if (seq filter-values)
              (contains? filter-values (get locator filter-name))
              true))
          filters))

(reg-sub
  ::filtered-locators
  (fn [[_ qpe-identity _search-string]]
    [(rf/subscribe [:current-project/all-locators qpe-identity])
     (rf/subscribe [::all-filters-enabled qpe-identity])])
  (fn [[locators filters] [_ _qpe-identity]]
    (if filters
      (filter #(does-match-filters % filters) locators)
      locators)))

(reg-sub
  ::sorted-locators
  (fn [[_ qpe-identity _sort-field _sort-order]]
    (rf/subscribe [::filtered-locators qpe-identity]))
  (fn [filtered-locators [_ _qpe-identity sort-field sort-order]]
    (let [column (get all-columns sort-field)]
      (cond-> (sort-by (:value-fn column) filtered-locators)
        (not sort-order) reverse))))

(defn sidebar-layout [props locator configuration]
  [:div.flex-grow-1
   {:style {:overflow-y "auto"
            :flex-basis "0"}}
   [:div
    (doall
      (for [[column config] configuration
            :let [{:keys [rendered label class]} (evaluate-locator-column config locator props)]]
        [:div.p-2.border-bottom
         {:key column}
         [:div.fw-bold label]
         [:div
          [:span.p-1
           {:class class}
           (or rendered "-")]]]))]])

(defn locator-details-content [props locator]
  (let [panel (-> props :parameters :query (:locator-panel "layout"))]
    [:<>
     [:ul.mt-2.px-2.nav.nav-tabs
      (doall
        (for [tab locator-sidebar-tabs]
          [:li.nav-item
           {:key (:id tab)}
           [:a.nav-link
            {:href "#"
             :on-click (fn [e]
                         (.preventDefault e)
                         (.stopPropagation e)
                         (rf/dispatch [:nav/update-query assoc :locator-panel (:id tab)]))
             :class    (when (= panel (:id tab))
                         "active")}
            (:label tab)]]))]
     (case panel
       "layout" [sidebar-layout props locator locator-columns-layout]
       "radio" [sidebar-layout props locator locator-columns-radio]
       "wireless" [sidebar-layout props locator locator-columns-wireless]
       "network" [sidebar-layout props locator locator-columns-network]
       nil)]))

(defn locator-details-tabs [{:keys [qpe-subscribe] :as props} locator-id]
  (let [locator @(qpe-subscribe [::locator-by-id locator-id])]
    (when locator
      [layout/sidebar
       [layout/sidebar-header
        {:title (or (:name locator)
                    locator-id)
         :on-close #(rf/dispatch [:nav/update-query dissoc :locator-id])}]
       ;; No sidebar-content, content is without padding.
       [locator-details-content props locator]])))

(defn locator-filtering [{:keys [qpe-subscribe qpe-dispatch]}]
  (let [all-filters-enabled @(qpe-subscribe [::all-filters-enabled])]
    [layout/sidebar
     [:div.d-flex.flex-column.border-bottom
      [:div.sidebar-header
       [:h3.sidebar-header-title "Filter locators"]
       ;; On desktop, close button keeps the changes
       [button/close-button
        {:class "p-3 d-none d-md-block"
         :on-click #(qpe-dispatch [::e/apply-panel-changes :locator-filters])}]
       ;; On mobile, close button cancels the changes
       [button/close-button
        {:class "p-3 d-block d-md-none"
         :on-click #(qpe-dispatch [::e/cancel-panel-changes :locator-filters])}]]

      [:div.sidebar-header-tools
       [:label.d-flex.align-items-center.m-0
        [:input {:type      "checkbox"
                 ;; Filters setting is empty, or is a map with every value being empty set.
                 :checked   (boolean (or (empty? all-filters-enabled)
                                         (every? empty? (vals all-filters-enabled))))
                 :on-change #(qpe-dispatch [::clear-filters])}]
        [:span.pl-3 "Show all"]]
       [:button.btn.btn-link.text-decoration-none
        {:on-click #(qpe-dispatch [::clear-filters])}
        "Clear filters"]]]

     [:div.sidebar-content.sidebar-content--scroll
      [:div.select-list.border-bottom
       [:h4 "Coordinate system"]
       (for [[filter-name number-of-rows] @(qpe-subscribe [::available-filter-values ::coordinate-system-name])]
         [:label
          {:key filter-name
           :on-mouse-down (fn [e] (.preventDefault e))}
          [:input {:type      "checkbox"
                   :checked   (-> all-filters-enabled ::coordinate-system-name (contains? filter-name) boolean)
                   :on-change #(qpe-dispatch [::add-filter ::coordinate-system-name filter-name (.. % -target -checked)])}]
          [:span.pl-3 filter-name]
          [:span.pl-3 number-of-rows]])]

      [:div.select-list.border-bottom
       [:h4 "Type"]
       (for [[filter-name number-of-rows] @(qpe-subscribe [::available-filter-values :hw-type])]
         [:label
          {:key filter-name
           :on-mouse-down (fn [e] (.preventDefault e))}
          [:input {:type      "checkbox"
                   :checked   (-> all-filters-enabled :hw-type (contains? filter-name) boolean)
                   :on-change #(qpe-dispatch [::add-filter :hw-type filter-name (.. % -target -checked)])}]
          [:span.pl-3 filter-name]
          [:span.pl-3 number-of-rows]])]

      [:div.select-list
       [:h4 "Connection"]
       (for [[filter-name number-of-rows] @(qpe-subscribe [::available-filter-values :command-status])]
         [:label
          {:key filter-name
           ;; Prevent text selection on double click
           :on-mouse-down (fn [e] (.preventDefault e))}
          [:input {:type      "checkbox"
                   :checked   (-> all-filters-enabled :command-status (contains? filter-name) boolean)
                   :on-change #(qpe-dispatch [::add-filter :command-status filter-name (.. % -target -checked)])}]
          [:span.pl-3
           (command-status-name filter-name)]
          [:span.pl-3 number-of-rows]])]]

     [:div.sidebar-actions
      [:button.btn.btn-outline-primary.btn-lg
       {:on-click #(qpe-dispatch [::e/cancel-panel-changes :locator-filters])}
       "Cancel"]
      [:button.btn.btn-primary.btn-lg.ml-3
       {:on-click #(qpe-dispatch [::e/apply-panel-changes :locator-filters])}
       "Apply"]]]))

(defn sidebar [{:keys [parameters] :as props}]
  (or
    (when-let [locator-id (-> parameters :query :locator-id)]
      [locator-details-tabs props locator-id])
    (when-let [sidebar-type (-> parameters :query :panel)]
      (case sidebar-type
        :locator-filters
        [locator-filtering props]
        :locator-columns
        [tables/columns-sidebar props
         {:k :locator-columns
          :default-columns default-columns
          :all-columns all-columns
          :max-selections 10}]
        nil))))

(defn locator-grid
  [{:keys [qpe-subscribe qpe-dispatch parameters] :as props}]
  (let [locators @(qpe-subscribe [:current-project/all-locators])
        sort-by-field (-> parameters :query (:sort-field :id))
        sort-order-asc (-> parameters :query (:sort-asc true))
        sorted-locators @(qpe-subscribe [::sorted-locators sort-by-field sort-order-asc])
        all-filters-enabled @(qpe-subscribe [::all-filters-enabled])
        connection-status-filters-available @(qpe-subscribe [::available-filter-values :command-status])
        [table-ref bounds] (useMeasure)

        selected-columns (or @(qpe-subscribe [:qpe/data :ui :locator-columns])
                             default-columns)
        selected-columns-width (->> selected-columns
                                    (map (fn [c]
                                           (:width (get all-columns c) 5)))
                                    (reduce + 0))]

    [layout/section
     [layout/view-content
      {:sidebar-open (or (:panel (:query parameters))
                         @(qpe-subscribe [:qpe/data :ui :sidebar :type]))}
      [layout/view-header
       {:title "Locators"}]

      [:div.d-flex.flex-row.my-2
       (doall
         (for [[connection-state number-available] connection-status-filters-available
               :let [active (-> all-filters-enabled :command-status (contains? connection-state))]]
           [:button.btn.mr-2
            {:key      (name connection-state)
             :class    (if active "btn-outline-primary" "btn-outline-secondary")
             :on-click #(qpe-dispatch [::add-filter :command-status connection-state (not active)])}
            (if active
              [icon :close.mr-2]
              [:div.qi-circle.mr-2
               {:class (case connection-state
                         :WaitingForStatus "text-warning"
                         :WaitingForReply "text-success"
                         :Ok "text-success"
                         "text-danger")}])

            (str number-available " " (command-status-name connection-state))]))]

      [:div.d-flex.flex-row.justify-content-between.py-2.align-items-center
       [:div
        [:span.align-middle.mr-4 "Showing " (count sorted-locators) " of " (count locators)]
        [:button.btn.btn-outline-secondary
         {:hidden    (nil? (some seq (vals all-filters-enabled)))
          :on-click (fn [_e]
                      (qpe-dispatch [::clear-filters]))}

         "Clear filters"
         [icon :close.ml-2]]]
       [:div.btn-toolbar.mx-n2
        [:button.btn.btn-outline-primary.mx-2
         {:on-click #(qpe-dispatch [::e/open-panel :locator-columns])}
         [icon :sliders.mr-3]
         "Columns"]
        [:button.btn.btn-outline-primary.mx-2
         {:on-click #(qpe-dispatch [::e/open-panel :locator-filters])}
         [icon :filter.mr-3]
         "Filter"]]]

      [tables/table-header
       {:bounds                 bounds
        :all-columns            all-columns
        :selected-columns       selected-columns
        :sort-by-field          sort-by-field
        :sort-order-asc         sort-order-asc
        :selected-columns-width selected-columns-width}]

      [:div.d-flex.flex-column
       {:ref   table-ref
        :style {:flex "1 0 0"}}
       [(r/adapt-react-class AutoSizer)
        {}
        (fn [dims]
          (let [height (gobj/get dims "height")
                width  (gobj/get dims "width")]
            (r/create-element
              FixedSizeList
              #js {:style     #js {:overflowY "scroll"}
                   :itemCount (count sorted-locators)
                   :itemSize  32
                   :itemKey   (fn [i _data] (:id (nth sorted-locators i)))
                   :height    (max 100 height)
                   :width     (max 100 width)}
              (r/reactify-component (partial locator-row
                                             selected-columns
                                             selected-columns-width
                                             (fn [i] (nth sorted-locators i))
                                             (gobj/get dims "width")
                                             props)))))]]]
     [sidebar props]]))

(def query-params
  [:map
   [:locator-id {:optional true} :string]
   [:locator-panel {:optional true} (into [:enum] (map :id locator-sidebar-tabs))]])

(def table-query-params
  (mu/merge tables/query-params query-params))
